import React, { createContext, useState, useEffect, useContext } from 'react'
import { toast } from 'react-hot-toast'
import { jwtDecode } from 'jwt-decode'
import { authAPI, cartAPI } from '../api'


const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState(() => localStorage.getItem('accessToken'))

  // Listen for storage changes (for cross-tab sync)
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('accessToken')
      setToken(newToken)
      if (!newToken) {
        setUser(null)
        setIsAuthenticated(false)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const currentToken = localStorage.getItem('accessToken')
      setToken(currentToken)

      if (!currentToken) {
        setLoading(false)
        return
      }

      // Check if token is expired
      const decoded = jwtDecode(currentToken)
      const currentTime = Date.now() / 1000

      if (decoded.exp < currentTime) {
        // Token expired, try to refresh
        await refreshToken()
      } else {
        // Token valid, get user info
        const response = await authAPI.getCurrentUser()
        setUser(response.data)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials)
      const { accessToken, refreshToken, user } = response.data

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      setToken(accessToken) // ✅ Update token state
      setUser(user)
      setIsAuthenticated(true)

      toast.success('Login successful!')

      if (user.role === 'ADMIN') {
        localStorage.removeItem('guestCart')
        window.location.href = '/admin/dashboard'
      } else {
        // Merge guest cart into server cart before redirecting
        const guestCart = localStorage.getItem('guestCart')
        if (guestCart) {
          try {
            const parsed = JSON.parse(guestCart)
            if (parsed.items?.length > 0) {
              // Add each guest item to server cart
              for (const item of parsed.items) {
                await cartAPI.addToCart({
                  productId: item.productId,
                  quantity: item.quantity
                })
              }
            }
          } catch (e) {
            console.error('Failed to merge guest cart:', e)
          } finally {
            localStorage.removeItem('guestCart')
          }
        }
        window.location.href = '/'
      }

      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed')
      throw error
    }
  }


  const socialLogin = async (userData) => {
    try {
      const response = await authAPI.socialLogin(userData)
      const { accessToken, refreshToken, user } = response.data

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      setToken(accessToken)
      setUser(user)
      setIsAuthenticated(true)

      toast.success('Welcome to WiseCart!')

      // Merge guest cart
      const guestCart = localStorage.getItem('guestCart')
      if (guestCart) {
        try {
          const parsed = JSON.parse(guestCart)
          if (parsed.items?.length > 0) {
            for (const item of parsed.items) {
              await cartAPI.addToCart({
                productId: item.productId,
                quantity: item.quantity
              })
            }
          }
        } catch (e) {
          console.error('Failed to merge guest cart:', e)
        } finally {
          localStorage.removeItem('guestCart')
        }
      }

      window.location.href = '/'
      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Social login failed')
      throw error
    }
  }

  const sendSignupOtp = async (email) => {
    try {
      const response = await authAPI.sendSignupOtp(email)
      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send verification code')
      throw error
    }
  }

  const verifySignupOtp = async (email, otp) => {
    return await authAPI.verifySignupOtp(email, otp)
  }

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData)
      const { accessToken, refreshToken, user } = response.data

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      setToken(accessToken) // ✅ Update token state
      setUser(user)
      setIsAuthenticated(true)

      toast.success(`Welcome to WiseCart, ${user.firstName}!`)
      window.location.href = '/'

      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed')
      throw error
    }
  }

  const logout = async () => {
    try {
      const currentToken = localStorage.getItem('accessToken')
      if (currentToken) {
        await authAPI.logout(currentToken)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setToken(null) // ✅ Clear token state
      setUser(null)
      setIsAuthenticated(false)
      toast.success('Logged out successfully')
      window.location.href = '/login'
    }
  }

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken')
      if (!refreshTokenValue) {
        throw new Error('No refresh token')
      }

      const response = await authAPI.refreshToken(refreshTokenValue)
      const { accessToken, refreshToken: newRefreshToken } = response.data

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', newRefreshToken)
      setToken(accessToken) // ✅ Update token state

      return accessToken
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      throw error
    }
  }

  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email)
      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reset code')
      throw error
    }
  }

  const resetPassword = async (email, otp, newPassword) => {
    try {
      const response = await authAPI.resetPassword(email, otp, newPassword)
      toast.success('Password reset successful! Please login.')
      window.location.href = '/login'
      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password')
      throw error
    }
  }

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await authAPI.verifyEmail(verificationToken)
      toast.success('Email verified successfully!')

      if (user) {
        setUser({ ...user, emailVerified: true })
      }

      return response
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify email')
      throw error
    }
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser)
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    token,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    refreshToken,
    checkAuth,
    updateUser,
    socialLogin,
    sendSignupOtp,
    verifySignupOtp
  }
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}